import { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Loader2, Star, Sparkles } from 'lucide-react';
import { Button } from '@/components/Button';
import { useTelegram } from '@/hooks/useTelegram';
import { fmtNum, mealTypeLabel } from '@/utils/format';
import { 
  addItemFromHistory, 
  findLatestItemIdByName, 
  type HistoryItem 
} from '@/hooks/useFoodHistory';
import { addProductToFavorites } from '@/hooks/useFavorites';
import type { MealType } from '@/types';
import { DayStrip } from '@/components/DayStrip';
import { consumedAtFor, dayPhrase } from '@/lib/mealDay';

interface LogHistoryItemModalProps {
  open: boolean;
  onClose: () => void;
  item: HistoryItem | null;
  onLogged?: (calories: number) => void;
  onSavedToFavorites?: () => void;
}

function getDefaultMealType(): MealType {
  const h = new Date().getHours();
  if (h < 11) return 'breakfast';
  if (h < 16) return 'lunch';
  if (h < 21) return 'dinner';
  return 'snack';
}

/**
 * Модалка добавления продукта из истории в дневник.
 * Аналогична LogFavoriteModal но работает с HistoryItem.
 * 
 * Дополнительно — кнопка "Сохранить в Мои блюда".
 */
export function LogHistoryItemModal({
  open,
  onClose,
  item,
  onLogged,
  onSavedToFavorites,
}: LogHistoryItemModalProps) {
  const { haptic } = useTelegram();
  const [weight, setWeight] = useState<string>('');
  const [mealType, setMealType] = useState<MealType>(getDefaultMealType());
  const [dayOffset, setDayOffset] = useState(0);
  const [saving, setSaving] = useState(false);
  const [savingToFav, setSavingToFav] = useState(false);

  // Сброс при открытии нового item
  useEffect(() => {
    if (open && item) {
      setWeight(String(item.avg_weight_g));
      setMealType(getDefaultMealType());
      setDayOffset(0);
    }
  }, [open, item]);

  // Расчёт КБЖУ в реальном времени по новому весу
  const computed = useMemo(() => {
    if (!item) return { cal: 0, p: 0, f: 0, c: 0, weight: 0 };
    const w = Number(weight) || 0;
    const factor = item.avg_weight_g > 0 ? w / item.avg_weight_g : 0;
    return {
      cal: Math.round(item.avg_calories * factor),
      p: +(item.avg_protein_g * factor).toFixed(1),
      f: +(item.avg_fat_g * factor).toFixed(1),
      c: +(item.avg_carbs_g * factor).toFixed(1),
      weight: w,
    };
  }, [item, weight]);

  if (!item) return null;

  async function handleSave() {
    if (!item) return;
    const w = Number(weight);
    if (!w || w < 1 || w > 5000) {
      alert('Вес должен быть от 1 до 5000 грамм');
      return;
    }
    
    setSaving(true);
    haptic('medium');
    
    try {
      // Находим source item id
      const sourceItemId = await findLatestItemIdByName(item.name);
      if (!sourceItemId) {
        haptic('error');
        alert('Не удалось найти продукт в истории. Возможно он был удалён.');
        return;
      }
      
      // День уходит в саму функцию (миграция 060). Раньше она всегда писала «сейчас»,
      // а дата правилась вторым запросом — и запись на завтра успевала засчитаться сегодня.
      const result = await addItemFromHistory({
        sourceItemId,
        newWeightG: w,
        mealType,
        consumedAt: consumedAtFor(dayOffset, mealType),
      });

      if (!result.success) {
        haptic('error');
        alert(`Не удалось добавить: ${result.error ?? 'ошибка'}`);
        return;
      }

      haptic('success');
      onLogged?.(computed.cal);
      onClose();
    } catch (e) {
      console.error(e);
      haptic('error');
      alert('Ошибка при сохранении');
    } finally {
      setSaving(false);
    }
  }
  
  async function handleSaveToFavorites() {
    if (!item) return;
    setSavingToFav(true);
    haptic('medium');
    
    try {
      // Пересчитываем на 100г для favorites
      const per100 = item.avg_weight_g > 0 ? 100 / item.avg_weight_g : 1;
      const result = await addProductToFavorites({
        name: item.name,
        calories_per_100g: Math.round(item.avg_calories * per100),
        protein_per_100g: +(item.avg_protein_g * per100).toFixed(1),
        fat_per_100g: +(item.avg_fat_g * per100).toFixed(1),
        carbs_per_100g: +(item.avg_carbs_g * per100).toFixed(1),
      });
      
      if (!result.success) {
        haptic('error');
        alert(`Не удалось сохранить: ${result.error}`);
        return;
      }
      
      haptic('success');
      onSavedToFavorites?.();
      // Не закрываем модалку — юзер может ещё добавить в дневник
    } catch (e: any) {
      haptic('error');
      alert(`Ошибка: ${e.message}`);
    } finally {
      setSavingToFav(false);
    }
  }

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm"
            onClick={onClose}
          />
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', stiffness: 350, damping: 30 }}
            className="fixed bottom-16 left-0 right-0 z-50 bg-bg rounded-t-3xl flex flex-col"
            style={{
              maxHeight: 'calc(100vh - 144px)',
              paddingBottom: 'env(safe-area-inset-bottom)',
            }}
          >
            {/* СКРОЛЛИРУЕМЫЙ КОНТЕНТ */}
            <div className="flex-1 overflow-y-auto px-5 pt-5 pb-4">
              <div className="max-w-md mx-auto">
                {/* Drag handle */}
                <div className="w-12 h-1 bg-line rounded-full mx-auto mb-4" />
                
                {/* Header */}
                <div className="flex items-start gap-3 mb-5">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-sky-glow/20 to-emerald-glow/15 flex items-center justify-center shrink-0">
                    <Sparkles size={20} className="text-sky-glow" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] font-bold text-sky-glow uppercase tracking-[1.5px] mb-0.5">
                      Из истории
                    </div>
                    <h2 className="font-display text-lg font-bold text-ink truncate">
                      {item.name}
                    </h2>
                    <p className="text-[11px] text-ink-dim mt-0.5">
                      Использовал ×{item.use_count} · обычно {item.avg_weight_g}г
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={onClose}
                    className="w-9 h-9 rounded-xl bg-bg-card border border-line flex items-center justify-center text-ink-muted"
                  >
                    <X size={18} />
                  </button>
                </div>

                {/* Сохранить в Мои блюда */}
                <button
                  type="button"
                  onClick={handleSaveToFavorites}
                  disabled={savingToFav}
                  className="w-full mb-4 py-2.5 rounded-xl border border-emerald-glow/30 bg-emerald-glow/5 hover:bg-emerald-glow/10 active:scale-[0.98] transition-all flex items-center justify-center gap-2 text-emerald-glow text-xs font-medium disabled:opacity-60"
                >
                  {savingToFav ? (
                    <Loader2 size={12} className="animate-spin" />
                  ) : (
                    <Star size={12} />
                  )}
                  {savingToFav ? 'Сохраняем...' : 'Сохранить в Мои блюда'}
                </button>

                {/* Вес */}
                <div className="mb-5">
                  <label className="label mb-2 block">Сколько грамм?</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={weight}
                      onChange={(e) => setWeight(e.target.value.replace(/\D/g, ''))}
                      className="input flex-1 text-lg font-display font-bold text-center"
                      placeholder="100"
                      autoFocus
                    />
                    <span className="text-ink-muted text-sm">г</span>
                  </div>
                  <p className="text-[11px] text-ink-dim mt-2">
                    💡 Обычно ты ешь {item.avg_weight_g}г
                  </p>
                </div>

                {/* Тип приёма */}
                <div className="mb-4">
                  <label className="label mb-2 block">Тип приёма</label>
                  <div className="grid grid-cols-4 gap-1.5">
                    {(['breakfast', 'lunch', 'dinner', 'snack'] as MealType[]).map((mt) => (
                      <button
                        type="button"
                        key={mt}
                        onClick={() => {
                          setMealType(mt);
                          haptic('light');
                        }}
                        className={`py-2 rounded-lg text-xs font-medium transition-colors ${
                          mealType === mt
                            ? 'bg-emerald-glow text-bg'
                            : 'bg-bg-card text-ink-muted border border-line'
                        }`}
                      >
                        {mealTypeLabel[mt]}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Когда */}
                <div className="mb-5">
                  <label className="label mb-2 block">{dayOffset > 0 ? 'На какой день' : 'Когда'}</label>
                  <DayStrip value={dayOffset} onChange={setDayOffset} bleed />
                </div>

                {/* Итого */}
                <div className="card-elevated p-4 border-emerald-glow/30">
                  <div className="flex items-baseline justify-between mb-2">
                    <span className="label">Итого</span>
                    <span className="font-display text-2xl font-bold text-emerald-glow">
                      {fmtNum(computed.cal)}
                      <span className="text-sm text-ink-muted font-body ml-1">ккал</span>
                    </span>
                  </div>
                  <div className="flex gap-3 text-xs">
                    <span className="text-emerald-glow">Б {fmtNum(computed.p, 1)}г</span>
                    <span className="text-warn">Ж {fmtNum(computed.f, 1)}г</span>
                    <span className="text-sky-glow">У {fmtNum(computed.c, 1)}г</span>
                    {computed.weight > 0 && (
                      <span className="text-ink-dim ml-auto">{computed.weight}г</span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* STICKY кнопка */}
            <div className="shrink-0 px-5 pt-3 pb-5 border-t border-line/30 bg-bg">
              <div className="max-w-md mx-auto">
                <Button
                  fullWidth
                  size="lg"
                  disabled={saving || computed.cal <= 0}
                  onClick={handleSave}
                  icon={saving ? <Loader2 size={18} className="animate-spin" /> : undefined}
                >
                  {saving
                    ? 'Сохраняем…'
                    : dayOffset !== 0
                      ? `Добавить ${dayPhrase(dayOffset)} · ${fmtNum(computed.cal)} ккал`
                      : `Добавить в дневник · ${fmtNum(computed.cal)} ккал`}
                </Button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
